import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { Ari } from '~/assets/icons';
import useStore from '~/store';
import { MessageBubble } from '../Message';
import { NoIcon } from '../styles';
import { Message } from '../types';
import { container, containerDefault, containerMessage, messageAfter } from './styles';

interface UserData {
  name: string;
  document_number: string;
  masked_email: string;
  masked_phone: string;
}

interface Props {
  content?: Message['content'];
  author?: Message['author'];
  questionComponent?: Message['questionComponent'];
  Component?: Message['Component'];
  userData?: UserData;
  userName?: string;
  onConfirm?: () => void;
  onDeny?: () => void;
  onNewEmailSubmit?: (cpfOrEmail: string) => void; // Pode receber CPF ou email dependendo do contexto
}

const OPTIONS = [
  {
    value: true,
    label: 'Sim'
  },
  {
    value: false,
    label: 'Não'
  }
];

const UserConfirmation = ({
  content: _content,
  questionComponent: _questionComponent,
  userData,
  userName,
  onConfirm,
  onDeny: _onDeny,
  onNewEmailSubmit
}: Props) => {
  const [userConfirmed, setUserConfirmed] = useState<boolean | null>(null);
  const [showCpfInput, setShowCpfInput] = useState(false);
  const [newCpf, setNewCpf] = useState('');

  // Acessa o store para atualizar o CPF
  const { SetCpf } = useStore((store) => ({
    SetCpf: store.SetCpf
  }));

  console.log('UserConfirmation - userData:', userData);
  console.log('UserConfirmation - userName:', userName);
  console.log('UserConfirmation - userData.name:', userData?.name);
  console.log('UserConfirmation - userData.masked_email:', userData?.masked_email);
  console.log('UserConfirmation - userData.masked_phone:', userData?.masked_phone);

  const handleUserConfirmation = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === 'true';
    setUserConfirmed(value);

    // Chama as funções de callback baseado na resposta
    if (value) {
      onConfirm && onConfirm();
    } else {
      setShowCpfInput(true);
      // onDeny será chamado quando o usuário submeter o novo CPF
    }
  };

  const handleNewCpfChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewCpf(event.target.value);
  };

  const handleNewCpfSubmit = () => {
    if (newCpf && onNewEmailSubmit) {
      // Atualiza o CPF no store
      SetCpf(newCpf);
      onNewEmailSubmit(newCpf);
    }
  };

  return (
    <Box sx={containerDefault}>
      {/* Mensagem do bot */}
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Ari />
        <MessageBubble
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', fontWeight: 'bold' }}>
              <Typography sx={{ marginBottom: '8px' }}>
                {userName?.split(' ')[0] || 'Usuário'}, localizamos o cadastro abaixo associado a esse CPF. Por
                segurança precisamos confirmar alguns dados.
              </Typography>

              {/* Nome */}
              <Box
                sx={{
                  background: '#333',
                  borderRadius: '10px',
                  padding: '0.85rem 0.75rem',
                  marginBottom: '8px'
                }}
              >
                <Typography sx={{ fontWeight: 'bold' }}>Nome: {userData?.name || 'Nome não disponível'}</Typography>
              </Box>

              {/* Email */}
              <Box
                sx={{
                  background: '#333',
                  borderRadius: '10px',
                  padding: '0.85rem 0.75rem',
                  marginBottom: '8px'
                }}
              >
                <Typography sx={{ fontWeight: 'bold' }}>
                  Email: {userData?.masked_email || 'Email não disponível'}
                </Typography>
              </Box>

              {/* Telefone */}
              <Box
                sx={{
                  background: '#333',
                  borderRadius: '10px',
                  padding: '0.85rem 0.75rem'
                }}
              >
                <Typography sx={{ fontWeight: 'bold' }}>
                  Telefone: {userData?.masked_phone || 'Telefone não disponível'}
                </Typography>
              </Box>
            </Box>
          }
        />
      </Box>

      {/* Pergunta de confirmação */}
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={NoIcon(96)} />
        <MessageBubble content="Seria você ?" />
      </Box>

      {/* Opções de resposta */}
      <Box sx={container}>
        <Box sx={() => containerMessage()}>
          <FormGroup>
            <FormControl>
              <RadioGroup
                aria-labelledby="radio-button-user-confirmation"
                value={userConfirmed !== null ? userConfirmed.toString() : ''}
                onChange={handleUserConfirmation}
              >
                {OPTIONS.map(({ value, label }) => (
                  <FormControlLabel
                    key={value.toString()}
                    value={value.toString()}
                    label={label}
                    control={
                      <Radio
                        inputProps={{ 'aria-label': label }}
                        checkedIcon={<CheckCircleIcon color="success" />}
                        sx={{ color: 'white' }}
                      />
                    }
                    sx={{ color: 'white' }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </FormGroup>
          <Box sx={messageAfter} />
        </Box>
      </Box>

      {/* Input para novo CPF quando usuário marca "Não" */}
      {showCpfInput && (
        <>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Box sx={NoIcon(96)} />
            <MessageBubble content="Por favor, informe outro CPF para verificarmos:" />
          </Box>

          <Box sx={container}>
            <Box sx={() => containerMessage()}>
              <TextField
                fullWidth
                type="text"
                value={newCpf}
                onChange={handleNewCpfChange}
                placeholder="Digite seu CPF"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: '#2A2A2A',
                    '& fieldset': {
                      borderColor: '#A547FF'
                    },
                    '&:hover fieldset': {
                      borderColor: '#A547FF'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#A547FF'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#CCCCCC',
                    opacity: 1
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleNewCpfSubmit();
                  }
                }}
              />
              <Box sx={messageAfter} />
            </Box>
          </Box>

          {/* Botão para verificar novo CPF */}
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            onClick={handleNewCpfSubmit}
            disabled={!newCpf}
            sx={{
              marginTop: '1.4rem',
              width: 'fit-content',
              marginInline: 'auto',
              backgroundColor: '#A547FF',
              '&:hover': {
                backgroundColor: '#8A3CD9'
              }
            }}
          >
            Verificar CPF
          </LoadingButton>
        </>
      )}
    </Box>
  );
};

export { UserConfirmation };
